import { readJsonPref, writeJsonPref } from '../localPrefs';

/**
 * "My calendars" (#681): a calendar is a tag, and the rail's unticked rows are
 * remembered as what is HIDDEN, never as what is picked (plan D2). A tag that
 * appears tomorrow therefore starts ticked: applying a new tag can never make
 * events vanish. Nothing stored means nothing hidden, which is today's calendar.
 *
 * Stored per user in this browser, with the same guarded mechanism as
 * `nurturecrm.calendar.viewMode` (plan D5): a preference, never org state.
 */
export const HIDDEN_TAGS_STORAGE_PREFIX = 'nurturecrm.calendar.hiddenTags';
/** Mirrors the server's HIDDEN_TAGS_CAP (services/calendar_event_tags.py). */
export const HIDDEN_TAGS_CAP = 50;

export const EMPTY_HIDDEN = Object.freeze({ tagIds: Object.freeze([]), untagged: false });

export function hiddenTagsStorageKey(userId) {
    return `${HIDDEN_TAGS_STORAGE_PREFIX}.${userId || 'anonymous'}`;
}

export function isValidHidden(value) {
    return Boolean(value)
        && typeof value === 'object'
        && Array.isArray(value.tagIds)
        && value.tagIds.length <= HIDDEN_TAGS_CAP
        && value.tagIds.every(id => typeof id === 'string' && id.length > 0 && id.length <= 64)
        && typeof value.untagged === 'boolean';
}

export function loadHiddenTags(userId) {
    return readJsonPref(hiddenTagsStorageKey(userId), EMPTY_HIDDEN, isValidHidden);
}

export function isAnythingHidden(hidden) {
    return Boolean(hidden && (hidden.untagged || hidden.tagIds.length > 0));
}

/** Nothing hidden removes the key, so the default stays the default. */
export function saveHiddenTags(userId, hidden) {
    writeJsonPref(hiddenTagsStorageKey(userId), isAnythingHidden(hidden) ? hidden : null);
}

/** Tick (`visible`) or untick a tag row. */
export function setTagVisible(hidden, tagId, visible) {
    const rest = hidden.tagIds.filter(id => id !== tagId);
    const tagIds = visible ? rest : [...rest, tagId].slice(-HIDDEN_TAGS_CAP);
    return { ...hidden, tagIds };
}

export function setUntaggedVisible(hidden, visible) {
    return { ...hidden, untagged: !visible };
}

/** Drop hidden ids that are no longer a tag (deleted or merged away). */
export function pruneHidden(hidden, knownIds) {
    const tagIds = hidden.tagIds.filter(id => knownIds.has(id));
    return tagIds.length === hidden.tagIds.length ? hidden : { ...hidden, tagIds };
}
